import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Plus } from '../../../icons';
import {
  WIDGET_DEFS, WIDGET_CAT_LABEL, type WidgetCat, type WidgetDef,
} from '../board/widget-registry';
import { WidgetChromeContext, DEFAULT_CHROME } from '../board/widget-chrome';
import { WidgetActionsContext, type WidgetActions } from '../board/widget-context';
// 대시보드 시안 v2 V7 (2026-09-16) — 줄마다 기본 폭 · 「담김」 · 미리보기 발 문구. 깃발이 꺼져 있으면 종전 모양 그대로.
import { DASHBOARD_V2_ENABLED } from '../../../lib/layout-shell';
import './widget-picker-v2.css';

/**
 * 위젯 고르기 — **좌 목록 · 우 «살아 있는 미리보기»** (2026-09-09 · 정본 F1 `picker`).
 *
 * ★★★ **왜 바꿨나 (사용자 지적).** 종전에는 카드에 손으로 그린 «도형»만 있어서
 *     「이름만 봐서는 어떤 화면인지 몰라 하나씩 올려 봐야 했다.」
 *     정본이 이미 답을 갖고 있다 — F1 의 `picker`(md 720 · 좌 240 고르기 · 우 480 상세,
 *     2026-09-07 신설 · 첫 사용 전자결재 「결재양식」). 새 무늬를 짓지 않고 그것을 채택한다.
 *
 * ★★★ **미리보기는 «진짜 위젯»이다.** 도형을 하나 더 그리면 그것이 또 실제와 어긋난다
 *     (판 5 에서 미니 도형 셋을 그리며 이미 겪었다). 실제 컴포넌트를 그대로 세우면
 *     로딩·빈 상태·「메뉴 관리에서 켜세요」까지 **있는 그대로** 보인다.
 *
 * ★★★ **한 번에 하나만 그린다.** 스물넷을 동시에 그리면 창을 여는 순간 API 를 스물넷 때린다 —
 *     그래서 «보고 있는 것» 하나만 그리고, 그 요청은 위젯 캐시(60초)가 대시보드와 나눠 쓴다.
 *
 * ★★ **«보는 것»과 «담는 것»은 다르다.** 줄을 누르면 오른쪽이 그 위젯으로 바뀌고(보기),
 *    체크나 「담기」를 눌러야 담긴다. 둘을 한 동작으로 묶으면 구경만 하려다 담기게 된다.
 * ★ 마우스를 올려도 미리보기가 따라간다 — 다만 **160ms 늦춘다**(목록을 훑고 지나가는
 *   중에 스물넷을 다 그리지 않게). 손이 없는 기기·키보드에서는 누르기가 곧 보기다.
 *
 * ⚠ 미리보기 안에서는 **아무 데도 못 간다** — 위젯의 행동을 빈 함수로 갈아 끼우고 클릭도
 *   막는다. 구경하다 대시보드 밖으로 튕겨 나가면 고르던 것이 통째로 사라진다.
 */

const CATS: WidgetCat[] = ['work', 'plan', 'talk', 'info'];
const SPAN_LABEL: Record<number, string> = { 4: '1/3', 6: '1/2', 8: '2/3', 12: '전체' };

/** 미리보기 안의 행동 — 전부 아무 일도 하지 않는다. */
const INERT: WidgetActions = {
  go: () => {},
  openAttendanceRequest: () => {},
  openSettings: () => {},
};

export interface WidgetPickerPaneProps {
  selected: string[];
  onToggle: (key: string) => void;
  /** 이미 얹혀 있는 위젯 — 표시만 다르게(중복으로 담는 것 자체는 막지 않는다). */
  alreadyOn?: string[];
}

export function WidgetPickerPane({ selected, onToggle, alreadyOn = [] }: WidgetPickerPaneProps) {
  const onBoard = useMemo(() => new Set(alreadyOn), [alreadyOn]);
  const [viewing, setViewing] = useState<string>(WIDGET_DEFS[0]?.key ?? '');
  const hoverTimer = useRef<number | null>(null);

  useEffect(() => () => { if (hoverTimer.current) window.clearTimeout(hoverTimer.current); }, []);

  const peek = (key: string) => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setViewing(key), 160);
  };
  const stopPeek = () => { if (hoverTimer.current) window.clearTimeout(hoverTimer.current); };

  const groups = CATS
    .map((cat) => ({ cat, items: WIDGET_DEFS.filter((d) => (d.cat ?? 'info') === cat) }))
    .filter((g) => g.items.length > 0);

  const def: WidgetDef | undefined = WIDGET_DEFS.find((d) => d.key === viewing);
  const picked = def ? selected.includes(def.key) : false;

  return (
    <div className="dwp">
      <div className="dwp__list" role="listbox" aria-label="위젯 목록" aria-activedescendant={`dwp-${viewing}`}>
        {groups.map(({ cat, items }) => (
          <div key={cat} className="dwp__group">
            <div className="dwp__ghead">{WIDGET_CAT_LABEL[cat]}</div>
            {items.map((d) => {
              const on = selected.includes(d.key);
              return (
                <div
                  key={d.key}
                  id={`dwp-${d.key}`}
                  role="option"
                  aria-selected={d.key === viewing}
                  tabIndex={-1}
                  className={`dwp__row${d.key === viewing ? ' is-viewing' : ''}${on ? ' is-on' : ''}`}
                  onMouseEnter={() => peek(d.key)}
                  onMouseLeave={stopPeek}
                  onClick={() => { stopPeek(); setViewing(d.key); }}
                >
                  <button
                    type="button"
                    className="dwp__name"
                    onClick={(e) => { e.stopPropagation(); stopPeek(); setViewing(d.key); }}
                  >
                    <d.icon size={14} aria-hidden />
                    <span className="dwp__t">{d.label}</span>
                    {!DASHBOARD_V2_ENABLED && onBoard.has(d.key) && <span className="dwp__used">사용 중</span>}
                  </button>
                  {/* ★ V7 — 줄 끝에 「사용 중」 또는 기본 폭. 이름 단추 «밖»에 둔다(단추 이름에 「1/3」이 섞이지 않게). */}
                  {DASHBOARD_V2_ENABLED && (
                    <em className={`dwpv-badge${onBoard.has(d.key) ? ' is-used' : ''}`}>
                      {onBoard.has(d.key) ? '사용 중' : (SPAN_LABEL[d.defaultSpan] ?? d.defaultSpan)}
                    </em>
                  )}
                  {/* ★ 「담기」는 «따로» 눌러야 한다 — 보는 것과 담는 것을 한 동작으로 묶지 않는다. */}
                  <button
                    type="button"
                    className={`dwp__pick${on ? ' is-on' : ''}`}
                    aria-pressed={on}
                    aria-label={`${d.label} ${on ? '빼기' : '담기'}`}
                    onClick={(e) => { e.stopPropagation(); onToggle(d.key); }}
                  >
                    {on ? <Check size={12} aria-hidden /> : <Plus size={12} aria-hidden />}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="dwp__detail">
        {def && (
          <>
            <div className="dwp__head">
              <span className="dwp__hname">{def.label}</span>
              <span className="dwp__chip">기본 폭 {SPAN_LABEL[def.defaultSpan] ?? def.defaultSpan}</span>
              {onBoard.has(def.key) && <span className="dwp__chip dwp__chip--used">이미 사용 중</span>}
            </div>
            <p className="dwp__hint">{def.hint}</p>

            {/* ★ 진짜 위젯이 선다. 행동은 빈 함수이고 클릭도 안 먹는다(구경만 하는 자리). */}
            <div className="dwp__stage" aria-hidden>
              <WidgetActionsContext.Provider value={INERT}>
                <WidgetChromeContext.Provider value={DEFAULT_CHROME}>
                  <def.Component />
                </WidgetChromeContext.Provider>
              </WidgetActionsContext.Provider>
            </div>

            {DASHBOARD_V2_ENABLED
              ? (
                /* ★ V7 — 발: 무엇을 보고 있는지 한 줄 + 「담김」. 이미 얹힌 위젯은 「하나 더 담기」(중복을 막지는 않는다). */
                <div className="dwpv-foot">
                  <span>실제 데이터로 그린 미리보기 · 누를 수 없음</span>
                  <button
                    type="button"
                    className={`dwp__add${picked ? ' is-on' : ''}`}
                    title={picked ? '다시 누르면 뺍니다' : undefined}
                    onClick={() => onToggle(def.key)}
                  >
                    {picked
                      ? <><Check size={14} aria-hidden />담김</>
                      : <><Plus size={14} aria-hidden />{onBoard.has(def.key) ? '하나 더 담기' : '이 위젯 담기'}</>}
                  </button>
                </div>
              )
              : (
                <button
                  type="button"
                  className={`dwp__add${picked ? ' is-on' : ''}`}
                  onClick={() => onToggle(def.key)}
                >
                  {picked ? <><Check size={14} aria-hidden />담았습니다 — 다시 누르면 뺍니다</> : <><Plus size={14} aria-hidden />이 위젯 담기</>}
                </button>
              )}
          </>
        )}
      </div>
    </div>
  );
}
